// IMPORTANT:
// Only import this file once, and only in `src/index.tsx` because we do not
// want to instantiate multiple query clients within our application.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type FC, type PropsWithChildren } from "react";

// Errors that fire when a stale mutation lands after the user was
// redirected to /login (no auth token in localStorage). Logging them
// via console.error sends them to RUM as a recurring, unactionable
// noise cluster (Datadog issue 3023973c-a83f-11ef-8278-da7ad0900002).
const SUPPRESSED_MUTATION_ERROR_MESSAGES = new Set([
  "Attempt to access private route without an auth token is forbidden.",
]);

/**
 * smartFetch throws the raw `Response` on non-OK HTTP. Logging that object
 * via console.error prints the useless string "Response" in Playwright /
 * Chromium (and RUM), while the UI already surfaces the API message. Skip
 * Response instances here; real Error failures still log.
 */
function shouldSuppressMutationConsoleError(e: unknown): boolean {
  if (typeof Response !== "undefined" && e instanceof Response) {
    return true;
  }
  return (
    e instanceof Error && SUPPRESSED_MUTATION_ERROR_MESSAGES.has(e.message)
  );
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
    },
    mutations: {
      onError(e) {
        if (shouldSuppressMutationConsoleError(e)) {
          return;
        }
        console.error(e);
      },
    },
  },
});

export const QueryClientContext: FC<PropsWithChildren> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
